import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { InputFieldType } from '../../models/input-types.enum';
import { HttpService } from 'src/app/core/services/api/http.service';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
  takeUntil,
  finalize,
  fromEvent,
  map,
  Subscription,
  tap,
} from 'rxjs';
import { FormGroup, Validators } from '@angular/forms';
import { ApiResponse } from '../../models/api-response.interface';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Dropdown } from 'primeng/dropdown';

@UntilDestroy()
@Component({
  selector: 'app-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('dropdown', { read: ElementRef }) dropdown!: ElementRef;

  @Input('disabled') isDisabled: boolean = false;
  @Input() form!: FormGroup;
  @Input() controlName!: string;
  @Input() label: string = '';
  @Input() labelTooltip?: string = '';
  @Input() mask: string = '';
  @Input() validation = false;
  @Input() placeholder: string = '';
  @Input() optionLabel: string = 'name';
  @Input() optionValue: string = 'id';
  @Input() type: InputFieldType = InputFieldType.Text;
  @Input() options: unknown[] = [];
  @Input() optionsLoading: boolean = false;
  @Input() optionsUrl?: string;
  @Input() searchParam: string = 'search';
  @Input() autoDisplayFirst = false;
  @Input() showBorder: boolean = false;
  @Input() showLabel: boolean = true;
  @Input() showFilter: boolean = false;
  @Output() onChange = new EventEmitter<any>();
  @Output() onScrollToEnd = new EventEmitter<any>();
  @Output() optionsLoaded = new EventEmitter<any[]>();
  @Input() editDealer: boolean = false;
  @Input() editProducts: any[] = [];
  @Input() defaultOptions: any[] = [];
  @Input() params: any = {};
  @Input() mapOptions: {
    new_key: string;
    keyOneName: string;
    keyTwoName: string;
  } = { new_key: '', keyOneName: '', keyTwoName: '' };
  @Input() fullNameField: boolean = false;
  @Input() isArray: boolean = false;
  @Input() timeOnly: boolean = false;
  @Input() shouldLoadOptionsInitially: boolean = true;
  @Input() shouldTrimInput: boolean = false;
  @Input() formattedOptions: boolean = false;
  searchValue: string = '';
  showPassword: boolean = false;
  private destroy$ = new Subject<void>();
  private searchEvent$ = new Subject<string>();
  protected readonly Validators = Validators;
  protected readonly InputFieldType: typeof InputFieldType = InputFieldType;
  randomId = Math.random();
  inputId = 'input-' + Math.random().toString(36).slice(2, 9);

  constructor(
    private apiService: HttpService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscribeOnChanges();
    if (
      this.type === InputFieldType.SearchableMultiSelect ||
      this.type === InputFieldType.SearchableDropdown ||
      this.type === InputFieldType.SearchableMultiSelectDropdown
    ) {
      this.subscribeOnSearchField();
      if (this.shouldLoadOptionsInitially) {
        this.getOptionsInitially();
      }
    }
  }

  emitScrollEnd(event: any) {
    const dropdownPanel = document.querySelector('.p-dropdown-items-wrapper');
    if (dropdownPanel) {
      dropdownPanel.addEventListener('scroll', () =>
        this.onScroll(dropdownPanel)
      );
    }
  }

  onScroll(dropdownPanel: any) {
    const scrollTop = dropdownPanel.scrollTop;
    const scrollHeight = dropdownPanel.scrollHeight;
    const clientHeight = dropdownPanel.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      this.onScrollToEnd.emit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['editProducts']) {
      this.editProducts = changes['editProducts']['currentValue'];
      if (this.editProducts.length > 0) {
        this.getProductsOnEdit(this.editProducts);
      }
    }

    const optionsUrlChanged =
      !!changes?.['optionsUrl']?.currentValue &&
      !changes?.['optionsUrl']?.firstChange;
    const paramsChanged =
      !!changes?.['params'] && !changes?.['params']?.firstChange;

    if (
      this.optionsUrl &&
      this.shouldLoadOptionsInitially &&
      (optionsUrlChanged || paramsChanged)
    ) {
      this.options = [];
      this.optionsLoaded.emit([]);
      this.currentPageScrolling = 1;
      this.lastPageScrolling = 1;
      this.searchValue = '';
      this.getOptionsInitially();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    this.cdr.markForCheck();
  }

  getProductsOnEdit(prodIds: any[]) {
    if (this.optionsUrl) {
      this.optionsLoading = true;
      this.apiService
        .getReq(this.optionsUrl, {
          params: {
            filter_by_id: this.editProducts.join(','),
          },
        })
        .pipe(catchError(() => of({ data: [] })))
        .subscribe((result: ApiResponse) => {
          this.options = result.data;
          this.optionsLoading = false;
          this.cdr.markForCheck();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getOptions(search: string): Observable<ApiResponse> {
    if (!this.optionsUrl) {
      return EMPTY;
    }
    this.optionsLoading = true;
    this.cdr.markForCheck();
    return this.apiService
      .getReq(this.optionsUrl, {
        params: {
          ...{ [this.searchParam]: search },
          ...this.params,
          page: this.currentPageScrolling,
        },
      })
      .pipe(catchError(() => of({ data: [] })));
  }

  handleSearch(search: string): void {
    const searchToSet = search.trim();
    if (!!this.optionsUrl) {
      this.currentPageScrolling = 1;
      this.searchValue = searchToSet;
      this.searchEvent$.next(searchToSet || '');
    }
  }

  private subscribeOnChanges(): void {
    this.form
      .get(this.controlName)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        this.onChange.emit(value);
      });
  }

  private subscribeOnSearchField(): void {
    this.searchEvent$
      .pipe(
        tap(() => {
          if (this.currentPageScrolling > 1) {
            this.loadingMoreGroups = true;
          } else if (this.currentPageScrolling === 1) {
            this.optionsLoading = true;
          }
          this.cdr.markForCheck();
        }),
        debounceTime(1000),
        // distinctUntilChanged(),
        switchMap((searchText: string) => {
          return this.getOptions(searchText);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result: ApiResponse) => {
        this.currentPageScrolling = result?.meta?.current_page
          ? result?.meta?.current_page
          : 1;
        this.lastPageScrolling = result?.meta?.last_page
          ? result?.meta?.last_page
          : 1;
        if (this.currentPageScrolling === 1) {
          this.options = this.getMapOptions(result.data);
          this.optionsLoading = false;
        } else {
          this.options = this.getMapOptions([...this.options, ...result.data]);
          this.loadingMoreGroups = false;
        }
        this.optionsLoaded.emit(this.options as any[]);
        this.cdr.markForCheck();
      });
  }

  private initialOptionsSubscription?: Subscription;

  getOptionsInitially() {
    this.initialOptionsSubscription?.unsubscribe();
    this.initialOptionsSubscription = this.getOptions('')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.optionsLoading = false))
      )
      .subscribe((result: ApiResponse) => {
        if (this.defaultOptions.length > 0) {
          this.options = [...this.defaultOptions, ...result.data];
        } else {
          this.options = result.data;
        }
        this.options = this.getMapOptions(this.options);
        this.optionsLoaded.emit(this.options as any[]);
        this.currentPageScrolling = result?.meta?.current_page
          ? result?.meta?.current_page
          : 1;
        this.lastPageScrolling = result?.meta?.last_page
          ? result?.meta?.last_page
          : 1;
        this.optionsLoading = false;
        this.cdr.markForCheck();
      });
  }

  getMapOptions(options: any[]) {
    if (this.mapOptions.new_key) {
      let newOptions = [];
      let i = 0;
      while (i < options.length) {
        let obj: any = { id: options[i]?.id };
        obj[this.mapOptions.new_key] =
          obj[this.mapOptions.keyOneName] +
          ' ' +
          obj[this.mapOptions.keyTwoName];
        newOptions.push(obj);
        i++;
      }
      return newOptions;
    } else if (this.fullNameField) {
      let fullNameOptions = options.map((i) => {
        return {
          ...i,
          label: `${
            i?.third_party_info?.name
              ? i?.third_party_info?.name
              : i?.first_name + ' ' + i?.last_name
          }`,
        };
      });
      return fullNameOptions;
    } else if (this.formattedOptions) {
      let formattedOptions = options.map((option) => ({
        id: option.id,
        [this.optionLabel]: option[this.optionLabel],
      }));
      return formattedOptions;
    } else if (this.isArray) {
      let formattedOptions = options.map((option) => ({
        id: option,
        label: option,
      }));
      return formattedOptions;
    } else {
      return options;
    }
  }

  listScrollSubscription: Subscription | null = null;
  currentPageScrolling: number = 1;
  lastPageScrolling: number = 1;
  loadingMoreGroups: boolean = false;

  setPagination() {
    const content = document.querySelector('.p-dropdown-items-wrapper');
    const scroll$ = fromEvent(content!, 'scroll').pipe(
      map(() => {
        return content!.scrollTop;
      })
    );
    this.listScrollSubscription = scroll$
      .pipe(debounceTime(1000))
      .subscribe((scrollPos) => {
        let limit = content!.scrollHeight - content!.clientHeight;
        if (Math.ceil(scrollPos) >= limit - 50) {
          if (this.currentPageScrolling < this.lastPageScrolling) {
            this.currentPageScrolling += 1;
            this.searchEvent$.next(this.searchValue);
          }
        }
      });
  }

  removeScrollSubscription() {
    if (this.listScrollSubscription) {
      this.listScrollSubscription.unsubscribe();
      this.listScrollSubscription = null;
    }
  }
}
